/**
 * Legacy event tracker shim.
 *
 * This module was the original fire-and-forget writer into `lead_events`.
 * It now ALSO forwards every call into the Customer Intelligence pipeline
 * (`customer_events` + `visitor_sessions`) so that existing call sites
 * (popup shown/dismissed, add_to_cart, etc.) transparently populate the
 * new analytics store. The lead_events write is kept so downstream popup
 * segmentation / retargeting jobs keep working.
 */
import { trackEvent } from "@/lib/identity.functions";
import { getAnonymousId } from "./anonymousId";
import { track as ciTrack } from "@/lib/customer-intelligence/tracker";
import { newEventId } from "@/lib/customer-intelligence/session";
import { createEventDelivery } from "./event-delivery";

type Payload = {
  eventType: string;
  properties?: Record<string, unknown>;
  route?: string;
  brand?: string;
};

type Delivery = Payload & { anonymousId: string; eventId: string };
const enqueue = createEventDelivery<Delivery>(
  (data) => trackEvent({ data }),
  (reason) => console.warn("[track] analytics", reason),
);

export function track(event: Payload) {
  if (typeof window === "undefined") return;
  const anonymousId = getAnonymousId();
  if (!anonymousId) return;
  const route = event.route ?? window.location.pathname;

  // New pipeline: batched, deduped, session-aware.
  try {
    ciTrack(event.eventType, {
      ...(event.properties ?? {}),
      route,
      ...(event.brand ? { brand: event.brand } : {}),
    });
  } catch (err) {
    if (import.meta.env.DEV) console.warn("[track] ci forward failed", err);
  }

  // The report still reads lead_events. Retries reuse one ID; the database
  // deduplicates acknowledgements lost in transit instead of inflating counts.
  enqueue({
    anonymousId,
    eventId: newEventId(),
    eventType: event.eventType,
    properties: event.properties,
    route,
    brand: event.brand,
  });
}
